import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  getSyllabusById,
  saveSyllabusItem,
} from '../api/syllabusApi'
import { getMasterOptionItems } from '../../../admission/students/api/mastersApi'
import { generateAcademicYears } from '../../lectures-list/hooks/useLectureEntryScreen'
import { appToast } from '../../../../components/AppToast'

export default function useSyllabusEntryScreen() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditMode = Boolean(id)

  const [loading, setLoading] = useState(isEditMode)
  const [saving, setSaving] = useState(false)

  // Master Options
  const [masterOptions, setMasterOptions] = useState({
    courses: [],
    subjects: [],
    schools: [],
  })

  // Form Fields
  const [academicYear, setAcademicYear] = useState('2026-27')
  const [course, setCourse] = useState('')
  const [subject, setSubject] = useState('')
  const [schoolCollege, setSchoolCollege] = useState('All')
  const [allSchools, setAllSchools] = useState(true)

  const [chapterNo, setChapterNo] = useState(1)
  const [chapterName, setChapterName] = useState('')
  const [hours, setHours] = useState(10)
  const [videoUrl, setVideoUrl] = useState('')

  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10))
  const [endDate, setEndDate] = useState(new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10))

  // Load Master Options (DRY from mastersApi)
  useEffect(() => {
    async function loadMasters() {
      try {
        const [cItems, sItems, scItems] = await Promise.all([
          getMasterOptionItems('courses'),
          getMasterOptionItems('subjects'),
          getMasterOptionItems('schools'),
        ])
        const cOpts = Array.isArray(cItems)
          ? cItems.map((c) => ({ value: String(c.id), label: c.name }))
          : []
        const sOpts = Array.isArray(sItems)
          ? sItems.map((s) => ({ value: String(s.id), label: s.name }))
          : []
        const scOpts = Array.isArray(scItems)
          ? scItems.map((sc) => ({ value: String(sc.id), label: sc.name }))
          : []

        setMasterOptions({
          courses: cOpts,
          subjects: sOpts,
          schools: scOpts,
        })
      } catch (err) {
        console.error('Failed to load masters for syllabus entry:', err)
      }
    }
    loadMasters()
  }, [isEditMode])

  // Load Entry Details in Edit Mode
  useEffect(() => {
    if (!isEditMode) return
    let active = true

    async function loadItem() {
      try {
        const item = await getSyllabusById(id)
        if (!item && active) {
          appToast.error('Syllabus entry not found.')
          navigate('/lectures/syllabus')
          return
        }
        if (item && active) {
          setAcademicYear(item.aYear || item.academicYear || '2026-27')
          setCourse(String(item.courseId || item.course_id || ''))
          setSubject(String(item.subjectId || item.subject_id || ''))
          const isAll = (item.schoolType || item.school_type) === 2 || !item.schoolId
          setAllSchools(isAll)
          setSchoolCollege(isAll ? 'All' : String(item.schoolId || item.school_id || ''))
          setChapterNo(item.chapterNo || item.chapter_no || 1)
          setChapterName(item.chapterName || item.chapter_name || '')
          setHours(item.hours || 10)
          setVideoUrl(item.video || item.videoUrl || '')
          setStartDate(item.startDate || item.start_date || '')
          setEndDate(item.endDate || item.end_date || '')
        }
      } catch (e) {
        console.error('Failed to load syllabus', e)
        appToast.error('Failed to load syllabus details.')
      } finally {
        if (active) setLoading(false)
      }
    }
    loadItem()
    return () => {
      active = false
    }
  }, [id, isEditMode, navigate])

  const handleAllSchoolsToggle = (checked) => {
    setAllSchools(checked)
    if (checked) {
      setSchoolCollege('All')
    } else {
      setSchoolCollege(masterOptions.schools[0]?.value || '')
    }
  }

  const resolveEntityId = (val, optionsList) => {
    if (!val) return 0
    const numeric = Number(val)
    if (!isNaN(numeric) && numeric > 0) return numeric
    const matched = optionsList.find(
      (opt) => opt.label.trim().toLowerCase() === String(val).trim().toLowerCase()
    )
    return matched ? Number(matched.value) : 0
  }

  const validate = () => {
    if (!academicYear) {
      appToast.error('Please select Academic Year.')
      return false
    }
    const courseId = resolveEntityId(course, masterOptions.courses)
    if (!courseId) {
      appToast.error('Please select Course.')
      return false
    }
    const subjectId = resolveEntityId(subject, masterOptions.subjects)
    if (!subjectId) {
      appToast.error('Please select Subject.')
      return false
    }
    if (!allSchools) {
      const schoolId = resolveEntityId(schoolCollege, masterOptions.schools)
      if (!schoolId) {
        appToast.error('Please select School / College.')
        return false
      }
    }
    if (!chapterNo || Number(chapterNo) <= 0) {
      appToast.error('Please enter a valid Chapter No.')
      return false
    }
    if (!chapterName.trim()) {
      appToast.error('Please enter Chapter / Topic Name.')
      return false
    }
    if (!startDate) {
      appToast.error('Please select Date From.')
      return false
    }
    if (!hours || Number(hours) <= 0) {
      appToast.error('Please enter valid Total Hours.')
      return false
    }
    return true
  }

  const handleSave = async (continueEntry = false) => {
    if (!validate()) return
    setSaving(true)

    const courseId = resolveEntityId(course, masterOptions.courses)
    const subjectId = resolveEntityId(subject, masterOptions.subjects)
    const schoolId = allSchools ? 0 : resolveEntityId(schoolCollege, masterOptions.schools)

    try {
      const payload = {
        id: isEditMode ? Number(id) : undefined,
        aYear: academicYear,
        courseId,
        subjectId,
        schoolType: allSchools ? 2 : 1,
        schoolId,
        chapterNo: Number(chapterNo),
        chapterName: chapterName.trim(),
        hours: Number(hours),
        video: videoUrl.trim(),
        startDate,
        endDate,
      }

      await saveSyllabusItem(payload)
      appToast.success(isEditMode ? 'Syllabus updated successfully.' : 'Syllabus saved successfully.')

      if (continueEntry) {
        setChapterNo((prev) => Number(prev) + 1)
        setChapterName('')
        setVideoUrl('')
      } else {
        navigate('/lectures/syllabus')
      }
    } catch (e) {
      console.error('Failed to save syllabus', e)
      const detail = e.response?.data?.detail || 'Failed to save syllabus entry.'
      appToast.error(detail)
    } finally {
      setSaving(false)
    }
  }

  return {
    state: {
      isEditMode,
      loading,
      saving,
      academicYear,
      course,
      subject,
      schoolCollege,
      allSchools,
      chapterNo,
      chapterName,
      hours,
      videoUrl,
      startDate,
      endDate,
      options: {
        academicYears: generateAcademicYears(),
        courses: masterOptions.courses,
        subjects: masterOptions.subjects,
        schools: masterOptions.schools,
      },
    },
    actions: {
      setAcademicYear,
      setCourse,
      setSubject,
      setSchoolCollege,
      handleAllSchoolsToggle,
      setChapterNo,
      setChapterName,
      setHours,
      setVideoUrl,
      setStartDate,
      setEndDate,
      handleSave,
    },
  }
}
